import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MessageCircle, ArrowUp, ExternalLink } from 'lucide-react';
import { api } from '@/lib/api';
import { trackCommunityBuzzImpression, trackCommunityBuzzClick } from '@/lib/analytics';

// ANALYTICS-057: Track impression once when component mounts with data
function useImpressionOnce(hasData: boolean) {
  const tracked = useRef(false);
  useEffect(() => {
    if (hasData && !tracked.current) {
      tracked.current = true;
      trackCommunityBuzzImpression();
    }
  }, [hasData]);
}

const SUBREDDIT_COLORS: Record<string, string> = {
  IndianBikes: 'text-orange-400 bg-orange-400/10',
  CarsIndia: 'text-blue-400 bg-blue-400/10',
};

export function CommunityBuzz() {
  const { data, isLoading } = useQuery({
    queryKey: ['community', 'buzz'],
    queryFn: () => api.community.buzz({ limit: 5 }),
    staleTime: 4 * 60 * 60 * 1000, // 4h — matches backend cache
  });

  const hasItems = !!data?.items?.length;
  useImpressionOnce(hasItems);

  if (!isLoading && !hasItems) return null;

  return (
    <div className="rounded-2xl border border-border-primary bg-bg-secondary overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border-primary bg-gradient-to-r from-red-500/5 to-transparent">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-500/10">
          <MessageCircle className="h-4 w-4 text-red-500" />
        </div>
        <h3 className="font-semibold text-text-primary">Community Buzz</h3>
      </div>

      <div className="p-2">
        {isLoading ? (
          <div className="space-y-3 p-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-4 w-full rounded bg-bg-tertiary" />
                <div className="mt-1 h-3 w-24 rounded bg-bg-tertiary" />
              </div>
            ))}
          </div>
        ) : (
          data?.items.map((post) => {
            const colorClass = SUBREDDIT_COLORS[post.subreddit] || 'text-text-tertiary bg-bg-tertiary';
            return (
              <a
                key={post.permalink}
                href={post.permalink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackCommunityBuzzClick(post.subreddit, post.permalink)}
                className="flex flex-col gap-1 rounded-lg px-3 py-2.5 transition-colors hover:bg-bg-tertiary group"
                data-testid="community-buzz-item"
              >
                <span className="text-sm font-medium text-text-secondary group-hover:text-primary-400 transition-colors line-clamp-2">
                  {post.title}
                </span>
                <div className="flex items-center gap-2 text-xs text-text-tertiary">
                  <span className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold ${colorClass}`}>
                    r/{post.subreddit}
                  </span>
                  <span className="flex items-center gap-0.5">
                    <ArrowUp className="h-3 w-3" />
                    {post.score}
                  </span>
                  <span className="flex items-center gap-0.5">
                    <MessageCircle className="h-3 w-3" />
                    {post.numComments}
                  </span>
                  <ExternalLink className="h-3 w-3 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </a>
            );
          })
        )}
      </div>
    </div>
  );
}
